import { makeStyles } from '@material-ui/core';
import { clsx } from '@components/Provider';
import { Modal, CustomizedTypography } from '@components/Customized';
import {
  CapaQuotationRfqPurchaseOrder,
  FetchPurchaseOrderPaymentData
} from '@generated/graphql';
import usePaymentModal from './usePaymentModal';
import usePurchaseOrder from '@components/Form/PurchaseOrder/PurchaseOrder/hooks/usePurchaseOrder';
import { PaymentModalProps } from './types';
import { formatDate } from '@utils/formatter';

interface PaymentDetailModalProps extends PaymentModalProps {
  paymentData: FetchPurchaseOrderPaymentData;
  purchaseOrder: CapaQuotationRfqPurchaseOrder;
}

const PaymentDetailModal: React.FunctionComponent<PaymentDetailModalProps> = ({
  openButton,
  paymentData,
  purchaseOrder
}) => {
  const { projectName } = usePurchaseOrder({
    rfqId: purchaseOrder?.rfqId,
    quotationPartnerId: purchaseOrder?.receiver?.quotationPartnerId
  });
  const { open, onClose, onOpen } = usePaymentModal();
  const paymentAmount =
    purchaseOrder?.paymentAmount?.toLocaleString('kr') ?? '';
  const partnerRegistrationNumber =
    purchaseOrder?.receiver?.companyRegistrationNumber ?? '';
  const partnerOrganizationName =
    purchaseOrder?.receiver?.organizationName ?? '';
  const comfirmedAt = formatDate(
    paymentData?.confirmedAt ?? '',
    'YYYY년 MM월 DD일'
  );
  const paidAt = formatDate(paymentData?.paidAt ?? '', 'YYYY년 MM월 DD일');

  return (
    <>
      {openButton(onOpen)}
      <Modal open={open} onClose={onClose}>
        <Modal.CloseButton onClose={onClose} />
        <Modal.Title>거래상세</Modal.Title>

        <Modal.ContentSection>
          <Field title="프로젝트명" value={projectName} gutterBottom />
          <Field
            title="파트너회사명"
            value={partnerOrganizationName}
            gutterBottom
          />
          <Field
            title="파트너 사업자 등록번호"
            value={partnerRegistrationNumber}
            gutterBottom
          />
          <Field title="결제 금액" value={`${paymentAmount}원`} gutterBottom />
          <Field title="결제일" value={paidAt} gutterBottom />
          <Field title="구매확정일" value={comfirmedAt} />
        </Modal.ContentSection>

        <Modal.ButtonSection>
          <Modal.Button color="default" onClick={onClose}>
            닫기
          </Modal.Button>
        </Modal.ButtonSection>
      </Modal>
    </>
  );
};

interface FieldProps {
  title: string;
  value: string;
  primary?: boolean;
  gutterBottom?: boolean;
}

const Field = ({ title, value, primary, gutterBottom }: FieldProps) => {
  const classes = useStyles();

  return (
    <div
      className={clsx(classes.fieldRoot, {
        gutterBottom
      })}
    >
      <CustomizedTypography
        variant="body2"
        color="textSecondary"
        gutterBottoms={2}
      >
        {title}
      </CustomizedTypography>
      <CustomizedTypography
        variant="subtitle1"
        color={primary ? 'primary' : 'textPrimary'}
      >
        {value}
      </CustomizedTypography>
    </div>
  );
};

export default PaymentDetailModal;

const useStyles = makeStyles(
  {
    fieldRoot: {
      display: 'flex',
      flexDirection: 'column',
      '&.gutterBottom': {
        marginBottom: 16
      }
    }
  },
  { classNamePrefix: 'payment-detail-modal' }
);
